import React, { useState } from "react";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { parseISO, format, isValid } from "date-fns";

import { space3, space4 } from "../../styled";

interface ScoreInputRowProps {
  label: string;
  value: number | null;
  max: number;
  onChange: (value: string) => void;
  step?: number;
  showMax?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
}

/**
 * Reusable score row with a bounded numeric input and max score indicator.
 */
export function ScoreInputRow({
  label,
  value,
  max,
  onChange,
  step = 1,
  showMax = true,
  readOnly = false,
  disabled = false,
}: ScoreInputRowProps) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={space4}>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>

      <Stack direction="row" alignItems="center" columnGap={showMax ? 0 : space3}>
        <TextField
          size="small"
          type="number"
          disabled={disabled}
          inputProps={{ min: 0, max, step, readOnly }}
          value={value ?? ""}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === "") {
              onChange(raw);
              return;
            }
            const parsed = Number(raw);
            if (isNaN(parsed)) return;
            const snapped = Math.round(parsed / step) * step;
            const clamped = Math.min(Math.max(snapped, 0), max);
            // Avoid floating-point drift (e.g. 0.30000000000000004)
            const precision = String(step).includes(".") ? String(step).split(".")[1].length : 0;
            onChange(precision > 0 ? clamped.toFixed(precision) : String(clamped));
          }}
          sx={{ width: 96 }}
          aria-label={label}
        />

        {showMax && (
          <Typography variant="body2" color="text.secondary" textAlign="right" sx={{ minWidth: "6ch" }}>
            / {max}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

interface ReadOnlyRowProps {
  label: string;
  /** Display value. Pass null / undefined to show an em-dash placeholder. */
  value: string | number | null | undefined;
}

/**
 * Displays a read-only label/value pair in the same visual style as ScoreInputRow.
 */
export function ReadOnlyRow({ label, value }: ReadOnlyRowProps) {
  const display = value !== null && value !== undefined ? String(value) : "—";
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={space4}>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: 96, textAlign: "right", fontStyle: "italic" }}
      >
        {display}
      </Typography>
    </Stack>
  );
}

interface AssessmentDatePickerProps {
  label?: string;
  value: string | null;
  onChange: (isoDate: string | null) => void;
}

/**
 * MUI DatePicker that stores and emits ISO datetime strings
 * (`yyyy-MM-dd'T'HH:mm:ss.SSSxxx`) normalized to local noon.
 * Supports both picker-based selection and manual keyboard entry.
 */
export function AssessmentDatePicker({
  label = "Administration date",
  value,
  onChange,
}: AssessmentDatePickerProps) {
  const [open, setOpen] = useState(false);
  const dateValue = value ? parseISO(value) : null;

  function handleChange(date: Date | null) {
    if (!date || !isValid(date)) {
      onChange(null);
    } else {
      date.setHours(12, 0, 0, 0); // DB requires DateTime, normalize to noon to avoid timezone issues
      onChange(format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"));
    }
  }

  return (
    <DatePicker
      label={label}
      value={dateValue}
      onChange={handleChange}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: false,
          InputLabelProps: { shrink: true },
          onClick: () => setOpen(true),
        },
      }}
    />
  );
}

interface FormActionRowProps {
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
}

/**
 * Shared cancel/submit footer actions for data entry forms.
 */
export function FormActionRow({ onCancel, onSubmit, submitLabel }: FormActionRowProps) {
  return (
    <Stack direction="row" justifyContent="flex-end" columnGap={space3}>
      <Button variant="contained" color="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="contained" color="primary" onClick={onSubmit}>
        {submitLabel}
      </Button>
    </Stack>
  );
}
